import React, { useState, useEffect, useRef } from "react";
import EventsActions from "./EventsActions";
import { AdminEvent } from "Types/Admin/AdminEventType";
import dayjs from "dayjs";
import { Trash2 } from "lucide-react";

type Props = {
  items: AdminEvent[];
  onView: (item: AdminEvent) => void;
  onEdit: (item: AdminEvent) => void;
  onDelete: (id: number) => void;
  onSelectionChange?: (selectedIds: number[]) => void;
  onBulkDelete?: () => void;
  selectedCount?: number;
};

const formatAdminDateTime = (date?: string, time?: string) => {
  if (!date || !time) return "—";
  const parsed = dayjs(`${date} ${time}`, "DD/MM/YYYY HH:mm");
  if (parsed.isValid()) return parsed.format("DD/MM/YYYY hh:mm A");
  const isoParsed = dayjs(time);
  if (isoParsed.isValid()) return isoParsed.format("DD/MM/YYYY hh:mm A");
  return "—";
};const EventsTable: React.FC<Props> = ({
  items,
  onView,
  onEdit,
  onDelete,
  onSelectionChange,
  onBulkDelete,
  selectedCount = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  useEffect(() => {
    setSelectedIds(new Set());
    onSelectionChange?.([]);
    setCurrentPage(1);
  }, [items]);

  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startItem = items.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, items.length);

  const paginatedItems = items.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = paginatedItems.map((item) => item.id);
      setSelectedIds(new Set(allIds));
      onSelectionChange?.(allIds);
    } else {
      setSelectedIds(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
    onSelectionChange?.(Array.from(newSelected));
  };

  const isAllSelected = paginatedItems.length > 0 && selectedIds.size === paginatedItems.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < paginatedItems.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);  return (
    <div className="w-full">
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onBulkDelete?.();
          }}
          disabled={selectedCount === 0}
          className={`transition-colors ${
            selectedCount > 0
              ? "text-red-600 hover:text-red-800 cursor-pointer"
              : "text-gray-300 cursor-not-allowed"
          }`}
          title="Delete Selected"
        >
          <Trash2 size={20} />
        </button>
      </div>

      <div className="relative overflow-x-auto">
        {isTransitioning && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center min-h-[200px]">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium text-gray-600">Loading...</span>
            </div>
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 text-left w-10">
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                Date & Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                Event Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                Event Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                Venue
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No events found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((it) => (
                <tr key={it.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-4 align-top">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedIds.has(it.id)}
                      onChange={(e) => handleSelectRow(it.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-4 py-4 align-top w-1/5 text-gray-800">
                    <div className="font-medium text-sm">{formatAdminDateTime(it.eventDate, it.eventStartTime)}</div>
                    <div className="text-gray-500 mt-1 text-xs">
                      Ends: {formatAdminDateTime(it.eventEndDate || it.eventDate, it.eventEndTime)}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top w-1/5 font-medium text-gray-800 text-sm">
                    {it.name || it.eventName || "No name"}
                  </td>
                  <td className="px-4 py-4 align-top w-2/5 text-gray-700 leading-relaxed text-sm">
                    {it.description || it.eventDescription || "No description"}
                  </td>
                  <td className="px-4 py-4 align-top w-1/5 text-gray-700 text-sm">
                    {it.eventAddress || it.venue || "No address"}
                  </td>
                  <td className="px-4 py-4 align-top w-28 text-right">
                    <EventsActions
                      onView={() => onView(it)}
                      onEdit={() => onEdit(it)}
                      onDelete={() => onDelete(it.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm font-body">
        <div className="hidden md:flex text-gray-600">
          Showing {startItem} to {endItem} of {items.length} entries
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Show</span>
            <select
              className="p-2 border rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-orange-400 transition-colors"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
                triggerTransition();
              }}
            >
              {[100, 500, 1000, 1500].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === 1 || isTransitioning}
              onClick={() => {
                setCurrentPage(1);
                triggerTransition();
              }}
              title="First Page"
            >
              &lt;&lt;
            </button>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === 1 || isTransitioning}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                triggerTransition();
              }}
              title="Previous Page"
            >
              &lt;
            </button>
            <div className="flex items-center justify-center min-w-[40px] h-9 bg-gray-50 border rounded-md font-medium text-orange-600">
              {currentPage}
            </div>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === totalPages || totalPages === 0 || isTransitioning}
              onClick={() => {
                setCurrentPage(currentPage + 1);
                triggerTransition();
              }}
              title="Next Page"
            >
              &gt;
            </button>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === totalPages || totalPages === 0 || isTransitioning}
              onClick={() => {
                setCurrentPage(totalPages);
                triggerTransition();
              }}
              title="Last Page"
            >
              &gt;&gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsTable;